import { useCallback, useMemo, useRef, useState } from "react";

export interface SubtitleRequestState {
    status: "loading" | "success" | "error";
    hasData: boolean;
    error?: string;
}

/** Only the newest request for the active media/folder/language may publish data or errors. */
export function useSubtitleRequest(contextKey: string) {
    const context = useRef({ key: contextKey, epoch: 0 });
    if (context.current.key !== contextKey) context.current = { key: contextKey, epoch: context.current.epoch + 1 };
    const sequence = useRef(0);
    const [result, setResult] = useState<SubtitleRequestState & { epoch: number }>({
        status: "loading", hasData: false, epoch: context.current.epoch,
    });
    const epoch = context.current.epoch;
    const state: SubtitleRequestState = useMemo(() => result.epoch === epoch
        ? result : { status: "loading", hasData: false }, [result, epoch]);
    const isCurrentContext = useCallback(() => context.current.epoch === epoch, [epoch]);

    // Возвращает, легли ли данные этого запроса: кто действует по перечитанной папке (раздвижка
    // озвучек после серверной озвучки), не должен действовать по старой копии.
    const run = useCallback(async <T>(request: () => Promise<{ data?: T; error?: string }>, accept: (data: T) => void): Promise<boolean> => {
        // A delayed caller (e.g. media initialization) may still hold a callback from the old view.
        if (context.current.epoch !== epoch) return false;
        const requestId = ++sequence.current;
        const current = () => context.current.epoch === epoch && sequence.current === requestId;
        setResult((previous) => ({ status: "loading", hasData: previous.epoch === epoch && previous.hasData, epoch }));
        try {
            const response = await request();
            if (!current()) return false;
            if (response.error || response.data === undefined) throw new Error(response.error || "Missing subtitle response");
            accept(response.data);
            if (current()) setResult({ status: "success", hasData: true, epoch });
            return true;
        } catch (error) {
            if (!current()) return false;
            setResult((previous) => ({ status: "error", hasData: previous.epoch === epoch && previous.hasData,
                error: error instanceof Error ? error.message : String(error), epoch }));
            return false;
        }
    }, [epoch]);
    return { state, run, isCurrentContext };
}
